import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneInput, Button } from '@unified-checkout/design-system';
import type { IField } from '@app/dashboard/types/outerLayout/common';
import type { IForgetPasswordPhoneNumberVerification } from '@app/dashboard/types/outerLayout/forget-password';
import {
  isFormValid,
  phoneNumberValidation,
  required
} from '@app/dashboard/utils/formValidations';
import { COUNTRIES_INFO } from '@app/dashboard/constants/countries';
import { forgetPasswordSubmitPhone } from '@app/dashboard/services/outer_layout/forget-password';
import { useEnterKeyPress } from '@app/dashboard/hooks/useEnterKeyPress';
import styles from "../forget-password.module.scss";

const FieldNames = {
  PHONE_NUMBER: "phone",
};

const SUBMIT_BTN_ID = 'forget-password-phone-number-verification-submit-btn';

const PhoneNumberVerification = (
  {
    setEmailOrPhoneNumber,
    validateField,
    loading,
    setLoading,
    setCurrentStep,
    STEPS,
    redirectToRegister,
    country
  }: IForgetPasswordPhoneNumberVerification
) => {
  const { t } = useTranslation();
  useEnterKeyPress(SUBMIT_BTN_ID);

  const fieldsInitialState = {
    [FieldNames.PHONE_NUMBER]: {
      value: '',
      isValid: undefined,
      errorMsg: '',
      validations: [required, phoneNumberValidation(country)]
    }
  };

  const [fields, setFields] = useState<IField>(fieldsInitialState);
  const [serverErrMsg, setServerErrMsg] = useState('');

  const handlePhoneChange = (number: string) => {
    setServerErrMsg("");
    const numWithoutPhoneCountryCode = number.replace(COUNTRIES_INFO[country].phone_country_code, '');

    const newState = fields;
    newState[FieldNames.PHONE_NUMBER].value = numWithoutPhoneCountryCode;

    setFields(newState);
    validateField(fields, setFields, FieldNames.PHONE_NUMBER, numWithoutPhoneCountryCode);
  };

  const handlePhoneSubmit = () => {
    setServerErrMsg("");
    setLoading(true);

    const phoneNumber = fields[FieldNames.PHONE_NUMBER].value as string;

    forgetPasswordSubmitPhone(phoneNumber)
      .then(() => {
        setCurrentStep(STEPS.OTP);
        setEmailOrPhoneNumber(phoneNumber);
      })
      .catch((error) => {
        const message = error.response?.data?.message;
        setServerErrMsg(message || t('errors.somethingWentWrong'))
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <PhoneInput
        id={FieldNames.PHONE_NUMBER}
        name={FieldNames.PHONE_NUMBER}
        label={t('outerLayout.form.labels.phoneNumber')}
        placeholder={t('outerLayout.form.register.placeholders.enterUrPhoneNum')}
        disableSelectCountry
        onChangeHandler={handlePhoneChange}
        country={COUNTRIES_INFO[country].country_code_iso2.toLowerCase()}
        error={fields[FieldNames.PHONE_NUMBER].errorMsg}
        disabled={loading}
      />
      <div className='form__submit'>
        {serverErrMsg && <div className='form__submit__server-err'>{serverErrMsg}</div>}
        <Button
          id={SUBMIT_BTN_ID}
          size='md'
          color='primary'
          variant='contained'
          text={loading ? undefined : t('outerLayout.form.next')}
          loading={loading}
          disabled={!isFormValid(fields) || loading}
          onClickHandler={handlePhoneSubmit}
        />
      </div>
      <div className={styles["forget-password-form__dont-have-account"]}>
        <span className={styles["forget-password-form__dont-have-account__text"]}>{t('outerLayout.form.dontHaveAcc')}</span>
        <div className={styles["forget-password-form__dont-have-account__action"]}>
          <Button
            size='sm'
            color='primary'
            variant='link'
            text={t('outerLayout.form.signUp')}
            onClickHandler={redirectToRegister}
          />
        </div>
      </div>
    </>
  )
}

export default PhoneNumberVerification;